/* eslint-disable import/no-unresolved */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Pagination,
} from 'swiper/modules';
import {
  Swiper,
  SwiperSlide,
} from 'swiper/react';

import 'swiper/css';
import 'swiper/css/pagination';

import {
  NextIcon,
  PrevIcon,
} from 'components/atoms/Icons/iconList';
import './carouselWithControls.scss';

const canLoop = (count, { slidesPerView = 1, slidesPerGroup = 1 } = {}) => (
  slidesPerView === 'auto'
    ? count > 1
    : count >= Math.ceil(slidesPerView) + slidesPerGroup
);

export default function CarouselWithControls({
  items,
  renderItem,
  slideClassName,
  breakpoints,
  paginationClassName,
  controlsClassName,
  loop,
  onNavigate,
}) {
  const swiperRef = useRef(null);
  const paginationRef = useRef(null);

  const [paginationReady, setPaginationReady] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    if (paginationRef.current) {
      setPaginationReady(true);
    }
  }, []);

  const itemCount = items?.length || 0;
  const loopBreakpoints = useMemo(() => Object.fromEntries(
    Object.entries(breakpoints || {}).map(([bp, config]) => [
      bp,
      { ...config, loop: !!loop && canLoop(itemCount, config) },
    ]),
  ), [breakpoints, loop, itemCount]);
  const baseLoop = !!loop && canLoop(itemCount, breakpoints?.[0]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper) return undefined;

    const sync = () => {
      setActiveIndex(
        typeof swiper.realIndex === 'number'
          ? swiper.realIndex
          : 0,
      );
      setLocked(!!swiper.isLocked);
      if (swiper.params.loop) {
        setAtStart(false);
        setAtEnd(false);
      } else {
        setAtStart(!!swiper.isBeginning);
        setAtEnd(!!swiper.isEnd);
      }
    };

    sync();

    swiper.on('slideChange', sync);
    swiper.on('reachBeginning', sync);
    swiper.on('reachEnd', sync);
    swiper.on('breakpoint', sync);
    swiper.on('lock unlock', sync);

    return () => {
      swiper.off('slideChange', sync);
      swiper.off('reachBeginning', sync);
      swiper.off('reachEnd', sync);
      swiper.off('breakpoint', sync);
      swiper.off('lock unlock', sync);
    };
  }, [loopBreakpoints, paginationReady]);

  if (!itemCount) return null;

  const prevDisabled = atStart;
  const nextDisabled = atEnd;

  return (
    <>
      {paginationReady && (
        <Swiper
          key={itemCount}
          modules={[Pagination]}
          watchOverflow
          loop={baseLoop}
          pagination={{
            el: paginationRef.current,
            clickable: true,
          }}
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
            swiper.params.pagination.el = paginationRef.current;
          }}
          onAfterInit={(swiper) => {
            swiper.pagination.init();
            swiper.pagination.render();
            swiper.pagination.update();
          }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={loopBreakpoints}
        >
          {items.map((item, index) => (
            <SwiperSlide
              key={item.order ?? index}
              className={slideClassName}
            >
              {renderItem(item, index, activeIndex)}
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      <div
        className={`
    mt-[28px]
    grid-cols-[40px_1fr_40px]
    items-center
    gap-[16px]
    md:justify-start
    ${locked ? 'hidden' : 'grid md:flex'}
    ${controlsClassName || ''}
  `}
      >
        <button
          type='button'
          aria-label='Previous'
          disabled={prevDisabled}
          onClick={() => { swiperRef.current?.slidePrev(); onNavigate?.(); }}
          className={`flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full border border-[#39A9F7] transition ${
            prevDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'hover:bg-[#39A9F7]/10'
          }`}
        >
          <PrevIcon color='#39A9F7' />
        </button>

        <div
          ref={paginationRef}
          className={`
      ${paginationClassName}
      flex justify-center
      md:hidden
    `}
        />

        <button
          type='button'
          aria-label='Next'
          disabled={nextDisabled}
          onClick={() => { swiperRef.current?.slideNext(); onNavigate?.(); }}
          className={`flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full border border-[#39A9F7] transition ${
            nextDisabled
              ? 'cursor-not-allowed opacity-40'
              : 'hover:bg-[#39A9F7]/10'
          }`}
        >
          <NextIcon color='#39A9F7' />
        </button>
      </div>
    </>
  );
}

CarouselWithControls.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  renderItem: PropTypes.func.isRequired,
  slideClassName: PropTypes.string,
  breakpoints: PropTypes.object,
  paginationClassName: PropTypes.string,
  controlsClassName: PropTypes.string,
  loop: PropTypes.bool,
  onNavigate: PropTypes.func,
};

CarouselWithControls.defaultProps = {
  slideClassName: '',
  breakpoints: {
    0: {
      slidesPerView: 1,
      spaceBetween: 16,
    },
    768: {
      slidesPerView: 'auto',
      spaceBetween: 24,
    },
  },
  paginationClassName: 'carousel-pagination',
  controlsClassName: '',
  loop: false,
  onNavigate: undefined,
};
